<template>
	<div class="team-panel">
		<section v-if="showUnlockAlert" class="team-panel__alert">
			<TeamingAlert type="INFO">{{ translate( `${ teamType }unlockAlert` ) }}</TeamingAlert>
		</section>
		<section v-if="showIncompleteAlert" class="team-panel__alert">
			<TeamingAlert type="INFO">{{ translate( `${ teamType }incompleteAlert` ) }}</TeamingAlert>
		</section>
		<h1 class="team-panel__heading">{{ translate( `${ teamType }heading` ) }}</h1>
		<header class="team-panel__head">
			<section class="team-panel__section team-panel__wheel-section">
				<div>
					<DISCWheel
						class="team-panel__disc-wheel"
						:activeWorkStyles="monikers"
					/>
				</div>
				<div>
					<p v-if="showTeamAssessment">{{ teamWorkStyleDescription }}</p>
					<p class="skeleton" v-if="!showTeamAssessment"><Skeleton animation="none" /></p>
					<p class="skeleton" v-if="!showTeamAssessment"><Skeleton animation="none" /></p>
					<p class="skeleton" v-if="!showTeamAssessment"><Skeleton animation="none" /></p>
					<p class="skeleton" v-if="!showTeamAssessment"><Skeleton animation="none" /></p>
					<p class="skeleton" v-if="!showTeamAssessment"><Skeleton animation="none" /></p>
				</div>
			</section>
		</header>
		<main class="team-panel__body">
			<section class="team-panel__section">
				<h2 class="team-panel__heading">{{ translate( "strengthsHeading" ) }}</h2>
				<ul v-if="showTeamAssessment">
					<li v-for="strength in teamStrengths" :key="strength">
						{{ strength }}
					</li>
				</ul>
				<p class="skeleton" v-if="!showTeamAssessment"><Skeleton animation="none" /></p>
				<p class="skeleton" v-if="!showTeamAssessment"><Skeleton animation="none" /></p>
				<p class="skeleton" v-if="!showTeamAssessment"><Skeleton animation="none" /></p>
				<p class="skeleton" v-if="!showTeamAssessment"><Skeleton animation="none" /></p>
				<p class="skeleton" v-if="!showTeamAssessment"><Skeleton animation="none" /></p>
			</section>
			<section class="team-panel__section">
				<h2 class="team-panel__heading">{{ translate( "blindSpotsHeading" ) }}</h2>
				<ul v-if="showTeamAssessment">
					<li v-for="blindSpot in teamBlindSpots" :key="blindSpot">
						{{ blindSpot }}
					</li>
				</ul>
				<p class="skeleton" v-if="!showTeamAssessment"><Skeleton animation="none" /></p>
				<p class="skeleton" v-if="!showTeamAssessment"><Skeleton animation="none" /></p>
				<p class="skeleton" v-if="!showTeamAssessment"><Skeleton animation="none" /></p>
				<p class="skeleton" v-if="!showTeamAssessment"><Skeleton animation="none" /></p>
				<p class="skeleton" v-if="!showTeamAssessment"><Skeleton animation="none" /></p>
			</section>
		</main>
	</div>
</template>

<script>
import range from "lodash/range";
import Skeleton from "primevue/skeleton";

import {
	TeamType
} from "@teaming/common-enums";

import {
	TranslateMixin,
	TeamingAlert,
	DISCWheel
} from "@teaming/common-ui";

const highTypeSortOrder = {
	D: 0,
	I: 1,
	S: 2,
	C: 3,
};

function highTypeSortComparator( a, b ) {
	return highTypeSortOrder[ a ] - highTypeSortOrder[ b ];
}

export default {
	name: "TeamPanel",
	teamingPackage: "common-team",
	mixins: [ TranslateMixin, ],
	components: {
		DISCWheel,
		Skeleton,
		TeamingAlert,
	},
	props: {
		teamType: {
			type: String,
			required: true,
			validator: value => TeamType.includes( value ),
		},
		assessments: {
			type: Array,
			required: true,
		},
		showTeamAssessment: {
			type: Boolean,
			required: true,
		},
		showUnlockAlert: {
			type: Boolean,
			required: true,
		},
		showIncompleteAlert: {
			type: Boolean,
			required: true,
		},
	},
	computed: {
		monikers() {
			if ( !this.showTeamAssessment ) {
				return [];
			}
			return this.assessments.map( assessment => assessment.moniker );
		},
		highTypes() {
			return [
				...this.assessments.reduce( ( acc, assessment ) => {
					acc.add( assessment.highType );
					return acc;
				}, new Set() ),
			].sort( highTypeSortComparator ).join( "" );
		},
		teamWorkStyleDescription() {
			const { teamType, highTypes, } = this;
			if ( !highTypes ) {
				return "";
			}
			const langKey = `${ teamType }workstyle${ highTypes }_description`;
			return this.translate( langKey );
		},
		teamStrengths() {
			const { teamType, highTypes, } = this;
			if ( !highTypes ) {
				return "";
			}
			// eslint-disable-next-line no-magic-numbers
			return range( 0, 5 ).map( i => {
				const langKey = `${ teamType }workstyle${ highTypes }_strength${ i }`;
				return this.translate( langKey );
			} );
		},
		teamBlindSpots() {
			const { teamType, highTypes, } = this;
			if ( !highTypes ) {
				return "";
			}
			// eslint-disable-next-line no-magic-numbers
			return range( 0, 5 ).map( i => {
				const langKey = `${ teamType }workstyle${ highTypes }_blindSpot${ i }`;
				return this.translate( langKey );
			} );
		},
	},
};
</script>

<style lang="scss" scoped>
.team-panel {
	display: flex;
	flex-direction: column;
	gap: 1rem;

	p, li {
		color: $team-panel-text-color;
		@include font-and-line-for(0);
	}

	p.skeleton {
		padding: 0.5rem 0;
	}

	p.skeleton:last-of-type {
		padding-bottom: 0;
		max-width: 50%;
	}

	ul {
		list-style: disc;
		padding: 0.5rem 1rem;
	}

	@include for-size('md') {
		gap: 2rem;
	}
}

.team-panel__heading {
	color: $team-panel-heading-color;
	@include font-and-line-for(1);
	font-weight: 600;
}

.team-panel__body {
	display: flex;
	flex-direction: column;
	justify-content: space-evenly;
	gap: 1rem;

	> section {
		width: 100%;
	}

	@include for-size('md') {
		flex-direction: row;
		gap: 2rem;
	}
}

.team-panel__wheel-section {
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	gap: 1rem;

	// wheel
	div:first-child {
		display: flex;
		flex-direction: row;
		justify-content: center;
		align-items: flex-start;
	}

	div:last-child {
		width: 100%;
	}

	@include for-size('md') {
		flex-direction: row;
		gap: 2rem;
	}
}

.team-panel__disc-wheel {
	max-width: 12rem; // 192px
	min-width: 12rem;

	@include for-size('md') {
		max-width: 10.5rem; // 168px
		min-width: 10.5rem;
	}
}
</style>
